import { createContext, useContext, type HTMLAttributes, type ReactNode, type TdHTMLAttributes, type ThHTMLAttributes } from 'react'
import { cn } from '../utils'

// Primitivas de tabela composáveis. Os ecrãs têm linhas próprias (seleção,
// menus, badges), por isso isto não é uma tabela configurada por colunas:
// é a mesma marcação <table>, com a densidade, o alinhamento numérico e os
// cabeçalhos decididos num só sítio.

type Density = 'compact' | 'comfortable'

const DensityContext = createContext<Density>('comfortable')

const cellPadding: Record<Density, string> = {
  compact: 'px-3 py-1.5 first:pl-6 last:pr-6',
  comfortable: 'px-3 py-2.5 first:pl-6 last:pr-6',
}

interface TableProps extends HTMLAttributes<HTMLTableElement> {
  density?: Density
  /** Cabeçalho fixo ao fazer scroll dentro de um contentor com overflow. */
  stickyHeader?: boolean
}

export function Table({ density = 'comfortable', stickyHeader = false, className, children, ...props }: TableProps) {
  return (
    <DensityContext.Provider value={density}>
      <table
        {...props}
        className={cn('w-full border-collapse text-sm', stickyHeader && '[&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-10', className)}
      >
        {children}
      </table>
    </DensityContext.Provider>
  )
}

export function TableHead({ className, children, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <thead {...props} className={cn('[&_tr]:border-b [&_tr]:border-slate-100', className)}>
      {children}
    </thead>
  )
}

export function TableBody({ className, children, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <tbody {...props} className={cn('[&_tr:last-child]:border-0', className)}>
      {children}
    </tbody>
  )
}

interface TableRowProps extends HTMLAttributes<HTMLTableRowElement> {
  selected?: boolean
  /** Linha clicável: cursor e hover. */
  interactive?: boolean
}

export function TableRow({ selected, interactive, className, children, ...props }: TableRowProps) {
  return (
    <tr
      {...props}
      aria-selected={selected || undefined}
      className={cn(
        'border-b border-slate-100 transition-colors',
        interactive && 'cursor-pointer hover:bg-surface',
        selected && 'bg-accent-50 hover:bg-accent-50',
        className,
      )}
    >
      {children}
    </tr>
  )
}

interface CellAlign {
  /** Valores numéricos/monetários: alinhados à direita e com algarismos tabulares. */
  numeric?: boolean
}

export function TableHeaderCell({ numeric, className, children, ...props }: ThHTMLAttributes<HTMLTableCellElement> & CellAlign) {
  const density = useContext(DensityContext)
  return (
    <th
      scope="col"
      {...props}
      className={cn(
        'bg-surface text-xs font-medium uppercase tracking-wide text-fg-muted',
        numeric ? 'text-right' : 'text-left',
        cellPadding[density],
        className,
      )}
    >
      {children}
    </th>
  )
}

export function TableCell({ numeric, className, children, ...props }: TdHTMLAttributes<HTMLTableCellElement> & CellAlign) {
  const density = useContext(DensityContext)
  return (
    <td
      {...props}
      className={cn(
        'text-slate-700',
        numeric && 'text-right tabular-nums',
        cellPadding[density],
        className,
      )}
    >
      {children}
    </td>
  )
}

export function TableEmpty({ colSpan, children }: { colSpan: number; children: ReactNode }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-6 py-10 text-center text-sm text-fg-muted">{children}</td>
    </tr>
  )
}
